import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Quotes — admin list of Large Projects company quotes, newest
// first, with a status filter.
//
// Consumes besthand-api `adminListQuotes(status, limit, offset)` (NEW admin
// query; returns `quotes`) through the `/lp-graphql` proxy via `lpApi`.
// Admin-gated server-side (requireAdmin).
// ============================================================================

const Q_QUOTES = `
  query ($status: String, $limit: Int, $offset: Int) {
    adminListQuotes(status: $status, limit: $limit, offset: $offset) {
      status errorMessage
      quotes {
        id projectRequestId companyId anonymousRef status paymentMode
        subtotal platformCommissionClient visitFee total currency
        warrantyDurationMonths totalDurationDays usesSubcontractors subcontractorsCount
        validUntil submittedAt selectedAt createdAt
      }
    }
  }
`;

// ADMIN action — void a quote so it can never be selected (blocked server-side
// if SELECTED into a live contract). Admin-gated (requireAdmin + gateLargeProjects).
const M_VOID_QUOTE = `
  mutation ($quoteId: String!, $reason: String) {
    adminVoidQuote(quoteId: $quoteId, reason: $reason) {
      status errorMessage
      quote { id status }
    }
  }
`;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'SELECTED', label: 'Selected' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'WITHDRAWN', label: 'Withdrawn' },
  { value: 'VOIDED', label: 'Voided' },
];

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  select: { padding: '9px 12px', border: '1px solid #d8dde2', borderRadius: 9, fontSize: 14, fontWeight: 600, outline: 'none', background: '#fff', cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  amount: { fontSize: 20, fontWeight: 800, color: '#0f1214', whiteSpace: 'nowrap' },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  modeTag: { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: '#eef2ff', color: '#3730a3', marginLeft: 8 },
  badge: (kind) => {
    const map = {
      SELECTED: { bg: '#e7f6ec', fg: '#137a37' },
      SUBMITTED: { bg: '#fff3d6', fg: '#946200' },
      DRAFT: { bg: '#eef2ff', fg: '#3730a3' },
      REJECTED: { bg: '#fde7e8', fg: '#c2151c' },
      WITHDRAWN: { bg: '#fde7e8', fg: '#c2151c' },
      VOIDED: { bg: '#fde7e8', fg: '#c2151c' },
      EXPIRED: { bg: '#eef2f4', fg: '#5a6168' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState('');

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async (statusArg) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_QUOTES, { status: statusArg || null, limit: 200, offset: 0 });
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.adminListQuotes;
        if (env && env.status === 200) setQuotes(env.quotes || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load quotes: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(status); }, [load, status]);

  const voidQuote = (q) => {
    const ok = typeof window !== 'undefined' && window.confirm(
      `VOID quote #${q.id} (Request #${q.projectRequestId}, ${q.anonymousRef || `company #${q.companyId}`})?\n\n`
      + 'A voided quote can never be selected into a contract. '
      + 'This is BLOCKED for a quote already selected into a live contract — cancel the contract instead.',
    );
    if (!ok) return;
    const reason = window.prompt(`Reason for voiding quote #${q.id} (optional):`);
    if (reason === null) return;
    setBusyId(`v${q.id}`);
    setMsg(null);
    lpApi(M_VOID_QUOTE, { quoteId: String(q.id), reason: reason.trim() || null })
      .then(async (res) => {
        if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
        const env = res.data && res.data.adminVoidQuote;
        if (env && env.status >= 200 && env.status < 300) { setMsg({ ok: `Quote #${q.id} voided.` }); await load(status); }
        else { setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` }); }
        setBusyId('');
      })
      .catch((e) => { setMsg({ error: `Action failed: ${e.message}` }); setBusyId(''); });
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Quotes</h2>
      <div style={S.hint}>
        Large Projects — company quotes against project requests, newest first. Filter by status.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btnGhost} onClick={() => load(status)} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{quotes.length} shown</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        quotes.length === 0 ? (
          <div style={S.empty}>No quotes for this filter.</div>
        ) : quotes.map((q) => (
          <div style={S.card} key={q.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>
                  Quote #{q.id}
                  {q.paymentMode ? <span style={S.modeTag}>{q.paymentMode}</span> : null}
                </p>
                <div style={S.meta}>
                  Request #{q.projectRequestId}
                  {q.companyId ? ` · Company #${q.companyId}` : ''}
                  {q.anonymousRef ? ` · ${q.anonymousRef}` : ''}
                </div>
                <div style={S.meta}>
                  {q.subtotal != null ? `Subtotal ${fmtMoney(q.subtotal, q.currency)} · ` : ''}
                  {q.platformCommissionClient != null ? `Commission ${fmtMoney(q.platformCommissionClient, q.currency)} · ` : ''}
                  {q.visitFee != null ? `Visit fee ${fmtMoney(q.visitFee, q.currency)} · ` : ''}
                  {q.totalDurationDays != null ? `${q.totalDurationDays}d · ` : ''}
                  {q.warrantyDurationMonths != null ? `${q.warrantyDurationMonths}mo warranty · ` : ''}
                  {q.usesSubcontractors ? `${q.subcontractorsCount || ''} subs · ` : ''}
                  Created {q.createdAt ? fmtDate(q.createdAt) : '—'}
                </div>
                <div style={S.meta}>
                  {q.submittedAt ? `Submitted ${fmtDate(q.submittedAt)} · ` : ''}
                  {q.selectedAt ? `Selected ${fmtDate(q.selectedAt)} · ` : ''}
                  {q.validUntil ? `Valid until ${fmtDate(q.validUntil)}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={S.amount}>{fmtMoney(q.total, q.currency)}</div>
                <span style={S.badge(q.status)}>{q.status || '—'}</span>
              </div>
            </div>
            {['DRAFT', 'SUBMITTED'].includes(q.status) && (
              <div style={S.actions}>
                <button style={S.btnRed} disabled={busyId === `v${q.id}`} onClick={() => voidQuote(q)}>
                  {busyId === `v${q.id}` ? '…' : 'Void quote'}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Quotes;
